import React from 'react';
import { Search, X, FilterX } from 'lucide-react';
import clsx from 'clsx';
import type {
  TableColumn,
  ColumnFilters,
  ColumnFilterValue,
  AnyOfFilter,
  DateRangeFilter as DateRangeFilterValue,
  NumberRangeFilter as NumberRangeFilterValue,
} from '@app-types';
import DateRangeFilter from './DateRangeFilter';
import NumberRangeFilter from './NumberRangeFilter';
import SelectFilter from './SelectFilter';
import field from './filterField.module.css';
import styles from './ColumnFiltersRow.module.css';

interface ColumnFiltersRowProps {
  columns: TableColumn[];
  filterValues: ColumnFilters;
  onFilterChange: (columnKey: string, value: ColumnFilterValue | null) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

/** The text a text-filter input should show for whatever value is held. */
function textValueOf(value: ColumnFilterValue | undefined): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  return value.kind === 'text' ? value.value : '';
}

function dateRangeValueOf(
  value: ColumnFilterValue | undefined
): DateRangeFilterValue | undefined {
  return typeof value === 'object' && value?.kind === 'dateRange' ? value : undefined;
}

function numberRangeValueOf(
  value: ColumnFilterValue | undefined
): NumberRangeFilterValue | undefined {
  return typeof value === 'object' && value !== null && value.kind === 'numberRange'
    ? value
    : undefined;
}


function anyOfValueOf(value: ColumnFilterValue | undefined): AnyOfFilter | undefined {
  return typeof value === 'object' && value?.kind === 'anyOf' ? value : undefined;
}

/**
 * Row of filter controls below the table header.
 *
 * Switches on `filterType` first, so a column can be both filterable and
 * custom-rendered. The previous order (`filterable ? text : filterNode ? ...`)
 * made those mutually exclusive, which is why typed filters could not coexist
 * with the filter row. `filterNode` is retained for the Reassessment column.
 */
const ColumnFiltersRow: React.FC<ColumnFiltersRowProps> = ({
  columns,
  filterValues,
  onFilterChange,
  onClearAll,
  hasActiveFilters
}) => {
  const renderFilter = (column: TableColumn) => {
    const label = typeof column.label === 'string' ? column.label : column.key;
    const value = filterValues[column.key];

    if (column.filterable === false) {
      return column.filterNode ?? <div className={styles.columnFilterPlaceholder} />;
    }

    if (column.filterType === 'dateRange') {
      return (
        <DateRangeFilter
          columnLabel={label}
          value={dateRangeValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
        />
      );
    }

    if (column.filterType === 'numberRange') {
      return (
        <NumberRangeFilter
          columnLabel={label}
          value={numberRangeValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
        />
      );
    }

    if (column.filterType === 'select') {
      return (
        <SelectFilter
          columnLabel={label}
          options={column.filterOptions ?? []}
          value={anyOfValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
        />
      );
    }

    const textValue = textValueOf(value);

    return (
      <div className={clsx(field.field, textValue && [field.hasValue, styles.hasValue])}>
        <Search size={14} className={styles.columnFilterIcon} aria-hidden="true" />
        <input
          type="text"
          className={styles.columnFilterInput}
          placeholder="Filter..."
          value={textValue}
          onChange={(e) =>
            onFilterChange(column.key, e.target.value ? { kind: 'text', value: e.target.value } : null)
          }
          aria-label={`Filter by ${label}`}
        />
        {/* One trailing slot, the same 18px every control in this row reserves:
            the Clear takes it only once there is something to clear. */}
        {textValue && (
          <button
            type="button"
            className={field.affordance}
            onClick={() => onFilterChange(column.key, null)}
            aria-label={`Clear ${label} filter`}
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <tr className={styles.columnFiltersRow}>
      {columns.map((column) => (
        <th key={column.key} className={styles.columnFilterCell}>
          {renderFilter(column)}
        </th>
      ))}
      {/* Sticky Clear All button - always visible even with horizontal scroll */}
      {hasActiveFilters && (
        <th className={clsx(styles.columnFilterCell, styles.columnFilterCellSticky)}>
          <button
            type="button"
            className={styles.clearAllFiltersBtn}
            onClick={onClearAll}
            title="Clear all filters"
          >
            <FilterX size={14} />
            <span>Clear</span>
          </button>
        </th>
      )}
    </tr>
  );
};

export default ColumnFiltersRow;
